import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/lib/authContext";
import { api, TimeEntry, PayPeriod } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, CalendarCheck, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { format, parseISO } from "date-fns";

export default function Submit() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [drafts, setDrafts] = useState<TimeEntry[]>([]);
  const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
  const [periodIndex, setPeriodIndex] = useState(0); // 0 = current (first in sorted list)

  const selectedPeriod = payPeriods[periodIndex] ?? null;

  const loadEntries = useCallback(async (period: PayPeriod) => {
    const entries = await api.getTimeEntries({
      startDate: period.start_date,
      endDate: period.end_date,
    });
    setDrafts(entries.filter((e) => e.status === "draft"));
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [allPeriods] = await Promise.all([api.getPayPeriods()]);
        // Sort descending (newest first), find current
        const sorted = [...allPeriods].sort(
          (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
        );
        setPayPeriods(sorted);
        const today = new Date().toISOString().slice(0, 10);
        const idx = sorted.findIndex((p) => today >= p.start_date && today <= p.end_date);
        const startIdx = idx >= 0 ? idx : 0;
        setPeriodIndex(startIdx);
        if (sorted[startIdx]) await loadEntries(sorted[startIdx]);
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [loadEntries]);

  // Load entries when period changes
  useEffect(() => {
    if (payPeriods[periodIndex]) loadEntries(payPeriods[periodIndex]);
  }, [periodIndex, payPeriods, loadEntries]);

  const draftHours = drafts.reduce((sum, e) => sum + e.minutes, 0) / 60;
  const isClosed = selectedPeriod?.status === "closed";

  const handleSubmit = async () => {
    if (drafts.length === 0) return;
    setIsSubmitting(true);
    try {
      const entryIds = drafts.map((e) => e.id);
      const result = await api.submitTimeEntries(entryIds);
      toast({
        title: "Timesheet Submitted",
        description: `Successfully submitted ${result.updated} entries (${draftHours.toFixed(2)} hours) for approval.`,
      });
      setDrafts([]);
    } catch (error: any) {
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit time entries.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8 pt-10">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-heading font-bold tracking-tight" data-testid="text-submit-title">Submit Timesheet</h1>
          <p className="text-muted-foreground">Review and submit your time for approval.</p>
        </div>

        {/* Period selector */}
        {payPeriods.length > 0 && (
          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPeriodIndex((i) => Math.min(i + 1, payPeriods.length - 1))}
              disabled={periodIndex >= payPeriods.length - 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[220px] text-center">
              {selectedPeriod
                ? `${format(parseISO(selectedPeriod.start_date), "MMM d")} – ${format(parseISO(selectedPeriod.end_date), "MMM d, yyyy")}`
                : "—"}
              {isClosed && (
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-rose-600 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">Closed</span>
              )}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPeriodIndex((i) => Math.max(i - 1, 0))}
              disabled={periodIndex <= 0}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {drafts.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-16 space-y-4 border rounded-lg bg-card">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="text-center space-y-1">
              <h2 className="text-xl font-heading font-semibold" data-testid="text-all-caught-up">
                {isClosed ? "Closed Period" : "No Draft Entries"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isClosed
                  ? "This pay period is closed and cannot be submitted."
                  : "No draft entries for this period. Use the arrows to navigate to another period."}
              </p>
            </div>
            <Button variant="outline" onClick={() => window.location.href = "/"} data-testid="button-back-to-today">Back to Today</Button>
          </div>
        ) : (
          <Card className="border-t-4 border-t-sidebar-primary shadow-xl">
            <CardHeader className="bg-muted/10 pb-8 border-b">
              <div className="flex flex-col items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-full">
                  <CalendarCheck className="w-8 h-8 text-primary" />
                </div>
                <div className="text-center">
                  <CardTitle className="text-xl">Ready to Submit?</CardTitle>
                  <CardDescription>You are about to submit the following entries.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-8 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg text-center border">
                  <div className="text-sm text-muted-foreground uppercase font-bold tracking-wider">Entries</div>
                  <div className="text-3xl font-bold font-heading" data-testid="text-entry-count">{drafts.length}</div>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg text-center border">
                  <div className="text-sm text-muted-foreground uppercase font-bold tracking-wider">Total Hours</div>
                  <div className="text-3xl font-bold font-heading text-primary" data-testid="text-total-hours">{draftHours.toFixed(2)}</div>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex gap-3 items-start">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-amber-800">Review Policy</h4>
                  <p className="text-sm text-amber-700">
                    Once submitted, entries cannot be edited without manager approval. Please verify your entries are correct.
                  </p>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-3 bg-muted/5 pt-6 pb-8">
              {isClosed ? (
                <div className="w-full text-center text-sm text-rose-600 font-medium py-3">
                  This pay period is closed — entries cannot be submitted.
                </div>
              ) : (
                <Button
                  size="lg"
                  className="w-full text-lg h-12 shadow-lg shadow-primary/20"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  data-testid="button-submit"
                >
                  {isSubmitting ? "Submitting..." : `Submit ${draftHours.toFixed(2)} Hours`}
                </Button>
              )}
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => window.location.href = "/"} data-testid="button-cancel">Cancel</Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </Layout>
  );
}
